import React, { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { u2000BuildAppRouteHref } from '../../../../../u2000Core/tools/u2000OpenAppRouteInNewTab';
import { isValidId } from '../../../../../u2000Core/tools/U2000IdUtils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import U2000DetailTable from '../../../../../u2000Core/components/tables/detail/U2000DetailTable';
import { U2000StringArrayDisplay } from '../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay';
import U2000CommonTableActions from '../../../../../u2000Core/components/tables/commons/U2000CommonTableActions';
import type { U2000TableColumn } from '../../../../../u2000Core/components/tables/basic/U2000BasicTableList';
import { useU2000GridStateHandlers } from '../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../../../../u2000Core/reducer/formReducer/U2000GridState';
import type { U2000SearchResult } from '../../../../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../../../U3121';
import {
  U3121_PROJECT_MINUTES_PAGE_SIZE,
  u3121BuildProjectMinutesSearchParameter,
  useU3121ProjectMinutesSearchHandlers,
} from '../../../../U3121UseProjectMinutesHandlers';
import {
  luHandleAppiError,
  u3139FormatFamilyLabel,
  u3139ResolveWorkNatureLabel,
  u3139SecondaryWorkNatureListLabels,
  type U3139EmployeeItem,
  type U3139MinutesSearchRow,
} from '../../../../../u3139Minutes/U3139Service';

function formatProjectMinuteSurveyorName(surveyor: U3139EmployeeItem | null | undefined): string {
  if (!surveyor) return '';
  return `${surveyor.firstName?.trim() ?? ''} ${surveyor.lastName?.trim() ?? ''}`.trim();
}

/** Id minute pour navigation U3139 (évite id?.trim() si id n'est pas une string). */
export function u3121ResolveProjectMinuteRowId(row: U3139MinutesSearchRow): string {
  const raw = row.id as unknown;
  if (typeof raw === 'string') return raw.trim();
  if (raw != null && typeof raw !== 'object') return String(raw).trim();
  return '';
}

const MINUTE_LINK_CLASS = 'text-blue-700 underline hover:text-blue-900 cursor-pointer';

function renderProjectMinuteNumberCell(
  t: TFunction,
  value: unknown,
  row: U3139MinutesSearchRow,
): React.ReactNode {
  const id = u3121ResolveProjectMinuteRowId(row);
  const label =
    value == null || String(value).trim() === '' ? (id || '—') : String(value);
  if (!isValidId(id)) return label;
  const openTitle = t('ttProjectMinuteOpenInNewTab', { ns: NS, defaultValue: 'Ouvrir la minute dans un nouvel onglet' });
  return (
    <a
      href={u2000BuildAppRouteHref(`/U3139/${id}`)}
      target="_blank"
      rel="noopener noreferrer"
      className={MINUTE_LINK_CLASS}
      title={openTitle}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
}

export interface U3121ProjectMinutesBodyProps {
  projectId: string;
}

function getColumnsDef(t: TFunction, languageCode: string): U2000TableColumn[] {
  return [
    {
      name: 'minuteNumber',
      label: t('lblProjectMinuteNumber', { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      noTruncate: true,
      display: (_column, value, row) =>
        renderProjectMinuteNumberCell(t, value, row as U3139MinutesSearchRow),
    },
    {
      name: 'workNature.family',
      label: t('lblProjectMinuteWorkNatureFamily', { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      display: (_column, _value, row) =>
        u3139FormatFamilyLabel((row as U3139MinutesSearchRow).workNature?.family, languageCode),
    },
    {
      name: 'workNature',
      label: t('lblProjectMinuteWorkNature', { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      display: (_column, _value, row) =>
        u3139ResolveWorkNatureLabel((row as U3139MinutesSearchRow).workNature, languageCode),
    },
    {
      name: 'secondaryWorkNatures',
      label: t('lblProjectMinuteSecondaryWorkNatures', { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: 'w-[280px]',
      type: 'string',
      align: 'left',
      noTruncate: true,
      display: (column, _value, row) =>
        createElement(U2000StringArrayDisplay, {
          column,
          row,
          value: u3139SecondaryWorkNatureListLabels(
            (row as U3139MinutesSearchRow).secondaryWorkNatures,
            languageCode,
          ),
        }),
    },
    {
      name: 'minuteDate',
      label: t('lblProjectMinuteDate', { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      widthClass: 'w-[120px]',
      type: 'date',
      align: 'left',
    },
    {
      name: 'surveyor',
      label: t('lblProjectMinuteSurveyor', { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      noTruncate: true,
      display: (_column, value) => formatProjectMinuteSurveyorName(value as U3139EmployeeItem | null | undefined),
    },
  ];
}

/**
 * Onglet Minutes du projet : liste lecture seule des minutes U3139 liées au projet (BL-0643).
 */
const U3121ProjectMinutesBody: React.FC<U3121ProjectMinutesBodyProps> = ({ projectId }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const gridStateHandlers = useU2000GridStateHandlers<U3139MinutesSearchRow>({
    nbRecordPerPage: U3121_PROJECT_MINUTES_PAGE_SIZE,
  });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const columns = useMemo(() => getColumnsDef(t, i18n.language), [t, i18n.language]);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const handleSearch = useU3121ProjectMinutesSearchHandlers({
    onSuccess: (searchResult: U2000SearchResult<U3139MinutesSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const preservedFields = gridStateHandlers.getSelectedFields();
      const defaultFieldNames = columnsRef.current
        .filter((col) => col.isDefaultColumn)
        .map((col) => col.name);
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      gridStateHandlers.setColumns(columnsRef.current);
      gridStateHandlers.setSize(U3121_PROJECT_MINUTES_PAGE_SIZE);
      // BL-0643 : fields: [] dans la requête ne doit pas vider les colonnes de la grille lecture seule
      if (searchResult.input.fields.length === 0) {
        gridStateHandlers.setSelectedFields(
          preservedFields.length > 0 ? preservedFields : defaultFieldNames,
        );
      }
      const rowsWithId = searchResult.data.map((row) => ({
        ...row,
        id: u3121ResolveProjectMinuteRowId(row) || row.id,
      }));
      gridStateHandlers.setData(rowsWithId);
      setLoadFailed(false);
      setLoading(false);
    },
    onError: ({ error }: { error: unknown }) => {
      setLoadFailed(true);
      setLoading(false);
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const searchPage = (
    page: number,
    direction: U2000GridStateEnumPaginationDirection = U2000GridStateEnumPaginationDirection.NEXTPAGE,
  ) => {
    const pid = projectId?.trim();
    if (!pid) {
      gridStateHandlers.setData([]);
      gridStateHandlers.setTotalRows(0);
      setLoading(false);
      setLoadFailed(false);
      return;
    }
    setLoading(true);
    void handleSearch.handleSearch(
      u3121BuildProjectMinutesSearchParameter(pid),
      { page, size: U3121_PROJECT_MINUTES_PAGE_SIZE },
      direction,
    );
  };

  useEffect(() => {
    gridStateHandlers.setSize(U3121_PROJECT_MINUTES_PAGE_SIZE);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(
      columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: 'ASC' as const })),
    );
    searchPage(1, U2000GridStateEnumPaginationDirection.NEXTPAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers / handleSearch exclus (montage onglet)
  }, [projectId, columns]);

  const totalRows = gridStateHandlers.getTotalRows();
  const pageSize = gridStateHandlers.getSize();
  const showPagination = totalRows > pageSize;

  if (loading && totalRows === 0 && !loadFailed) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">{t('msgLoading', { ns: NS })}</p>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-gray-600">{t('msgProjectMinutesLoadError', { ns: NS })}</p>
      </div>
    );
  }

  if (totalRows === 0) {
    return <p className="py-8 text-sm text-gray-600">{t('msgProjectMinutesEmpty', { ns: NS })}</p>;
  }

  return (
    <div className="space-y-3">
      {showPagination ? (
        <U2000CommonTableActions
          gridStateHandlers={gridStateHandlers}
          actionsButtons={null}
          onPaginationChanged={(page) => {
            searchPage(page, U2000GridStateEnumPaginationDirection.NEXTPAGE);
          }}
        />
      ) : null}
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={false}
        label={t('lblProjectMinutesCount', { count: totalRows, ns: NS })}
      />
    </div>
  );
};

export default U3121ProjectMinutesBody;
